/* -----------------Globals--------------- */
import { ScrollView } from "react-native";

/* -----------------Helpers & Hooks--------------- */
import useSessionConfig, {
	type AdditionalParams,
} from "../hooks/useSessionConfig";

import type { SessionConfigFiltersType } from "@configs/custom-app-config-schema";
import { diagnosticTagIds } from "@features/session/helpers/session";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import type { Distribution } from "@memorang/types/src/content";
import type { PracticeTestExam } from "@memorang/types/src/tag";
import Box from "@memorang/ui/src/components/Box";
import StickyButtonContainer from "@memorang/ui/src/components/StickyButtonContainer";
import type { DiagnosticItem } from "api/types";
/* -----------------Child components--------------- */
import DiagnosticItemsContainer from "containers/DiagnosticItemsContainer";
import DistributionFilterList from "../components/DistributionFilterList";
import ItemSlider from "../components/ItemSlider";
import ItemTypesSelector from "../components/ItemTypesSelector";
import TagFiltersList from "../components/TagFiltersList";
import useLoadingMessages from "../hooks/useLoadingMessages";
import SessionSummary from "./SessionSummary";

type Props = {
	available: Distribution;
	additionalParams?: AdditionalParams;
	handleCreateSession: (distribution: Distribution, tags: number[]) => void;
	creatingSession?: boolean;
	handleShowPurchase: () => void;
	handleClose?: () => void;
	hasAccess?: boolean;
	diagnosticItems?: DiagnosticItem[];
	exams?: PracticeTestExam[];
	distributionFilterItems: SessionConfigFiltersType;
};

const SessionConfig = ({
	available,
	additionalParams,
	handleCreateSession,
	creatingSession,
	handleShowPurchase,
	hasAccess,
	exams,
	handleClose,
	distributionFilterItems,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	const showLearningModes = useAppStore(
		(state) => state.app.sessionConfig?.showLearningMode,
	);
	const showDistributionFilters = true;
	const showItemsSlider = useAppStore(
		(state) => state.app.sessionConfig?.showItemsSlider,
	);
	const {
		handleSetTagCheckboxMap,
		selectedTagCheckboxMap,
		numRequiredItems,
		requiredDistribution,
		selectedLearningMode,
		handleSetLearningMode,
		finalAvailableDist,
		selectedDistributionCheckboxMap,
		handleDistributionCheckboxState,
		availableItems,
		numTotalItemsFiltered,
		handleSliderChange,
	} = useSessionConfig({
		availableDistribution: available,
		additionalParams: additionalParams || {},
		isSessionCreationInProgress: creatingSession,
		showLearningMode: showLearningModes,
		showDistributionFilters,
		distributionFilterItems,
	});

	const ctaText = useLoadingMessages(Boolean(creatingSession));

	const filterSelectedDiagnosticTags =
		selectedTagCheckboxMap &&
		Object.keys(selectedTagCheckboxMap).filter(
			(value) =>
				selectedTagCheckboxMap[value] &&
				Object.values(diagnosticTagIds).includes(Number(value)),
		);

	const isDiagnosticTagSelected =
		filterSelectedDiagnosticTags && filterSelectedDiagnosticTags.length > 0;

	const handleStartSession = () => {
		const selectedTags = selectedTagCheckboxMap
			? Object.keys(selectedTagCheckboxMap).filter(
					(tagId) => selectedTagCheckboxMap[tagId],
				)
			: [];

		const unselectedTags =
			additionalParams?.hasAccess && selectedTagCheckboxMap
				? Object.keys(selectedTagCheckboxMap || {}).filter(
						(tagId) => !selectedTagCheckboxMap[tagId],
					)
				: additionalParams?.freeTagIds;
		const finalTagIds =
			selectedTags?.length > 0 ? selectedTags : unselectedTags;

		if (!creatingSession) {
			handleCreateSession(requiredDistribution, finalTagIds?.map(Number) || []);
		}
	};

	const showSummary = numRequiredItems > 0;

	return (
		<StickyButtonContainer
			buttonText={ctaText}
			icon={creatingSession ? undefined : "play-outline"}
			onPress={handleStartSession}
			loading={creatingSession}
			showButton={!(!hasAccess && exams?.length)}
			disabled={!numRequiredItems || creatingSession}
		>
			<ScrollView
				contentContainerStyle={{
					paddingVertical: 16,
					paddingHorizontal: isWeb ? 16 : 0,
				}}
			>
				<Box gap={16} flex={1}>
					{!showLearningModes && (
						<DiagnosticItemsContainer
							exams={exams}
							hasAccess={hasAccess}
							handleClose={handleClose}
							handleShowPurchase={handleShowPurchase}
							paddingHorizontal={16}
							isSessionConfig
							disableAll={creatingSession}
						/>
					)}
					{showLearningModes && additionalParams?.numItemsMap ? (
						<ItemTypesSelector
							selectedLearningMode={selectedLearningMode}
							disableAll={creatingSession}
							handleSetLearningMode={handleSetLearningMode}
							numItemsMap={additionalParams.numItemsMap}
						/>
					) : null}
					{showItemsSlider && (
						<ItemSlider
							label={"Session length"}
							availableItems={availableItems}
							requiredItems={numRequiredItems}
							disabled={creatingSession}
							mode={selectedLearningMode}
							numTotalItemsFiltered={numTotalItemsFiltered}
							handleSliderChange={handleSliderChange}
							testID="session-length-slider"
						/>
					)}
					{showDistributionFilters && (
						<DistributionFilterList
							distribution={finalAvailableDist}
							hasAccess={
								hasAccess || Boolean(additionalParams?.freeTagIds?.length)
							}
							disableAll={creatingSession}
							selectedCheckboxMap={selectedDistributionCheckboxMap}
							handleSelectCheckbox={handleDistributionCheckboxState}
							distributionFilterItems={distributionFilterItems}
							handleShowPurchase={handleShowPurchase}
						/>
					)}
					{additionalParams?.sectionTags ? (
						<TagFiltersList
							disableAll={creatingSession || isDiagnosticTagSelected}
							selectedCheckboxMap={selectedTagCheckboxMap}
							handleSelectCheckbox={(distKey, locked) => {
								if (locked) {
									handleShowPurchase();
								} else {
									handleSetTagCheckboxMap(distKey);
								}
							}}
							hasAccess={hasAccess}
							showPremiumChip={!hasAccess}
							sectionTags={additionalParams.sectionTags}
						/>
					) : null}
				</Box>
			</ScrollView>
			{showSummary && (
				<SessionSummary
					numRequiredItems={numRequiredItems}
					selectedDistributionCheckboxMap={selectedDistributionCheckboxMap}
					selectedTagCheckboxMap={selectedTagCheckboxMap}
					sectionTags={additionalParams?.sectionTags}
				/>
			)}
		</StickyButtonContainer>
	);
};

export default SessionConfig;
